import React, { useState, useRef, useEffect } from 'react';
import ReactPlayer from 'react-player';
import styled from 'styled-components';
import DownloadableFile from '../DownloadableFile';
import Flex from '../Flex';
import Icon from '../Icon';
import IconAsButton from '../IconAsButton';
import { getLighterThemeColor, toRgba } from '../theme/colors';
import { media } from '../../utils/media';
import { useTranslation } from 'react-i18next';
import theme from '../theme';

// Styled components
const PlayerWrapper = styled.div`
  position: relative;
  padding-top: 56.25%; /* 16:9 aspect ratio */
  background: ${({ $themeColor }) => $themeColor};
`;

const StyledPlayer = styled(ReactPlayer)`
  position: absolute;
  top: 0;
  left: 0;
`;

const ControlsWrapper = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to bottom, ${toRgba(theme.colors.NEU_999, 0)} 0%, ${toRgba(theme.colors.NEU_999, 0.5)} 60%);
  padding: ${({ $isMinimal }) => ($isMinimal ? '4px' : '0 8px')};
  display: flex;
  flex-direction: column;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 0.3s ease;
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
`;

const ProgressBar = styled.input`
  width: 100%;
  cursor: pointer;
  height: ${({ $isMinimal }) => ($isMinimal ? '3px' : '4px')};
  accent-color: ${({ $themeColor }) => $themeColor};
`;

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ $isMinimal }) => ($isMinimal ? '8px' : '15px')};

  ${media.lessThan('tablet')} {
    gap: ${({ $isMinimal }) => ($isMinimal ? '6px' : '10px')};
  }

  ${media.lessThan('phablet')} {
    gap: ${({ $isMinimal }) => ($isMinimal ? '5px' : '8px')};
  }
`;

const VolumeSlider = styled.input`
  width: 80px;
  height: 5px;
  cursor: pointer;
  accent-color: ${({ $themeColor }) => $themeColor};

  ${media.lessThan('tablet')} {
    width: 60px;
  }

  ${media.lessThan('phablet')} {
    width: 50px;
  }
`;

const VolumeContainer = styled.div`
  display: flex;
  align-items: center;
`;

const TimeDisplay = styled.span`
  font-size: ${({ $isMinimal }) => ($isMinimal ? '10px' : '12px')};
  color: ${theme.colors.NEU_0};
  text-align: center;

  ${media.lessThan('tablet')} {
    min-width: ${({ $isMinimal }) => ($isMinimal ? '50px' : '60px')};
    font-size: ${({ $isMinimal }) => ($isMinimal ? '9px' : '10px')};
  }
`;

const BigPlayButton = styled.button`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: ${toRgba(theme.colors.NEU_999, 0.5)};
  border: none;
  border-radius: 50%;
  padding: ${({ $isMinimal }) => ($isMinimal ? '6px' : '10px')};
  cursor: pointer;
  transition: transform 0.2s ease, background 0.2s ease;

  &:hover {
    background: ${toRgba(theme.colors.NEU_999, 0.7)};
    transform: translate(-50%, -50%) scale(1.1);
  }
`;

// Custom hook to observe player width
const usePlayerWidth = (ref) => {
  const [width, setWidth] = useState(null);

  useEffect(() => {
    if (!ref.current) return;
    const resizeObserver = new ResizeObserver((entries) => {
      setWidth(entries[0].contentRect.width);
    });
    resizeObserver.observe(ref.current);
    return () => resizeObserver.disconnect();
  }, [ref]);

  return width;
};

const VideoPlayer = ({ url, themeColor, posterImage, handleError, downloadFile }) => {
  const { t } = useTranslation();
  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const [previousVolume, setPreviousVolume] = useState(0.8);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showControls, setShowControls] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const playerRef = useRef(null);
  const wrapperRef = useRef(null);

  const playerWidth = usePlayerWidth(wrapperRef);
  const isMinimal = playerWidth && playerWidth < 320;
  const lightThemeColor = getLighterThemeColor(themeColor);

  const handlePlay = () => {
    setPlaying(true);
    setHasStarted(true);
  };

  const handlePause = () => {
    setPlaying(false);
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    setPreviousVolume(newVolume);
    setIsMuted(newVolume === 0);
  };

  const handleProgress = (state) => {
    if (!state.seeking) {
      setProgress(state.played * 100);
    }
  };

  const handleDuration = (duration) => {
    setDuration(duration);
  };

  const handleSeek = (e) => {
    const seekTo = parseFloat(e.target.value) / 100;
    playerRef.current.seekTo(seekTo);
    setProgress(parseFloat(e.target.value));
  };

  const formatTime = (seconds) => {
    if (!isFinite(seconds)) return '0:00';
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const toggleMute = () => {
    if (isMuted) {
      setVolume(previousVolume || 0.8);
    } else {
      setPreviousVolume(volume);
      setVolume(0);
    }
    setIsMuted(!isMuted);
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      // Enter fullscreen
      if (wrapperRef.current) {
        if (wrapperRef.current.requestFullscreen) {
          wrapperRef.current.requestFullscreen();
        } else if (wrapperRef.current.webkitRequestFullscreen) {
          wrapperRef.current.webkitRequestFullscreen();
        } else if (wrapperRef.current.msRequestFullscreen) {
          wrapperRef.current.msRequestFullscreen();
        }
      }
    } else {
      // Exit fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement || !!document.webkitFullscreenElement || !!document.msFullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
  }, []);

  const renderPlayPauseButton = () =>
    playing ? (
      <IconAsButton
        iconName='Pause'
        clickHandle={handlePause}
        color={lightThemeColor}
        size={isMinimal ? 'small' : undefined}
        tooltipLabel={t('globals.pause')}
      />
    ) : (
      <IconAsButton iconName='Play' clickHandle={handlePlay} color={lightThemeColor} size={isMinimal ? 'small' : undefined} tooltipLabel={t('globals.play')} />
    );

  const renderVolumeControl = () => (
    <VolumeContainer>
      <IconAsButton
        iconName={isMuted ? 'VolumeOff' : 'VolumeOn'}
        tooltipLabel={isMuted ? t('globals.unmute') : t('globals.mute')}
        color={lightThemeColor}
        clickHandle={toggleMute}
      />
      <VolumeSlider type='range' min={0} max={1} step={0.01} value={volume} onChange={handleVolumeChange} $themeColor={lightThemeColor} />
    </VolumeContainer>
  );

  const renderControls = () => {
    const leftControls = (
      <Flex align='center' gap={isMinimal ? 8 : 15}>
        {renderPlayPauseButton()}
        <TimeDisplay $isMinimal={isMinimal}>
          {formatTime((progress / 100) * duration)} / {formatTime(duration)}
        </TimeDisplay>
      </Flex>
    );

    const rightControls = (
      <Flex align='center' gap={isMinimal ? 8 : 15}>
        {!isMinimal && renderVolumeControl()}
        {downloadFile && (
          <DownloadableFile.AsComponent file={downloadFile}>
            <Icon.Download color={lightThemeColor} size='small' />
          </DownloadableFile.AsComponent>
        )}
        <IconAsButton
          iconName={isFullscreen ? 'FullscreenExit' : 'Fullscreen'}
          clickHandle={toggleFullscreen}
          color={lightThemeColor}
          size={isMinimal ? 'small' : undefined}
          tooltipLabel={isFullscreen ? t('globals.exitFullscreen') : t('globals.enterFullscreen')}
        />
      </Flex>
    );

    return (
      <ControlsWrapper $visible={isControlsVisible} $themeColor={lightThemeColor} $isMinimal={isMinimal}>
        <ProgressBar type='range' min={0} max={100} value={progress} onChange={handleSeek} $themeColor={lightThemeColor} $isMinimal={isMinimal} />
        <ControlsRow $isMinimal={isMinimal}>
          {leftControls}
          {rightControls}
        </ControlsRow>
      </ControlsWrapper>
    );
  };

  const isControlsVisible = hasStarted && (showControls || !playing);

  const onError = (error) => {
    const canPlay = ReactPlayer.canPlay(url);
    !canPlay && handleError && handleError(error);
  };

  return (
    <PlayerWrapper ref={wrapperRef} onMouseEnter={() => setShowControls(true)} onMouseLeave={() => setShowControls(false)} $themeColor={lightThemeColor}>
      <StyledPlayer
        ref={playerRef}
        url={url}
        playing={playing}
        volume={volume}
        muted={isMuted}
        controls={false}
        width='100%'
        height='100%'
        light={!!posterImage && !hasStarted ? posterImage : false}
        playIcon={null}
        config={{ file: { attributes: { poster: posterImage || undefined } } }}
        onProgress={handleProgress}
        onDuration={handleDuration}
        onError={onError}
        onPlay={handlePlay}
        onPause={handlePause}
        onClickPreview={null}
      />
      {!hasStarted && (
        <BigPlayButton onClick={handlePlay} $themeColor={lightThemeColor} $isMinimal={isMinimal} aria-label='Play video'>
          <Icon.Play size={isMinimal ? 'large' : 'xxl'} color={lightThemeColor} />
        </BigPlayButton>
      )}
      {renderControls()}
    </PlayerWrapper>
  );
};

export default VideoPlayer;
